import { loyaltyApi } from '@vmos2/core';
import { useRequest } from '@vmos2/utils';
import type { MutableRefObject } from 'react';
import { useCallback, useState } from 'react';

import { USER_PROMOTIONS_CACHE_ID } from '../constants';

type UserPromotions = Record<string, any>[];

type UserPromotionsResponse = {
  meta?: Record<string, any>;
  payload: UserPromotions;
};

const initialState = {
  isLoadingImages: false,
  promotionsBeingApplied: [],
  userPromotionsMeta: undefined,
};

const defaultUserPromotions: UserPromotions = [];

export const useUserPromotions = (isLoyaltyEnabled: MutableRefObject<boolean>) => {
  const [isLoadingImages, setIsLoadingImages] = useState(initialState.isLoadingImages);
  const [promotionsBeingApplied, setPromotionsBeingApplied] = useState<string[]>(initialState.promotionsBeingApplied);
  const apiCache = loyaltyApi.storage.data[USER_PROMOTIONS_CACHE_ID];

  const [userPromotionsMeta, setUserPromotionsMeta] = useState<Record<string, any> | undefined>(
    initialState.userPromotionsMeta
  );

  const [{ data, isLoading }, fetchUserPromotions, resetRequest] = useRequest<UserPromotions>(
    useCallback(
      async ({ apiCacheData, cache, requestInProgress }) => {
        if (isLoyaltyEnabled.current) {
          const meta = (userPromotionsMeta && { meta: { ...userPromotionsMeta } }) || {};

          const response: UserPromotionsResponse =
            apiCacheData || await loyaltyApi.post(
              'promotions/user',
              { products: [], ...meta },
              { authProviderToken: true, cache, id: USER_PROMOTIONS_CACHE_ID, unwrap: true }
            );

          const responseData = response.payload;

          if (requestInProgress.current) {
            setUserPromotionsMeta(response?.meta);
          }

          const userPromotionImages = responseData
            ?.map(userPromotion => userPromotion.image)
            .filter((image): image is string => Boolean(image));

          if (userPromotionImages?.length) {
            if (requestInProgress.current) {
              setIsLoadingImages(true);
            }

            await Promise.all(userPromotionImages.map(userPromotionImage =>
              new Promise(resolve => {
                const image = new Image();

                image.src = userPromotionImage;

                if (image.complete) {
                  resolve(userPromotionImage);
                } else {
                  image.onload = resolve;
                  image.onerror = resolve;
                }
              })
            ));

            if (requestInProgress.current) {
              setIsLoadingImages(false);
            }
          }

          return responseData;
        }

        return Promise.resolve(defaultUserPromotions);
      },
      [isLoyaltyEnabled, userPromotionsMeta]
    ),
    // 3600000 = 1 hour
    { apiCache, defaultCache: { override: true, ttl: 3600000 }, preserveDataWhileLoading: true }
  );

  const clearUserPromotions = useCallback(async () => {
    resetRequest();
    await loyaltyApi.storage.remove(USER_PROMOTIONS_CACHE_ID);
    setIsLoadingImages(initialState.isLoadingImages);
    setPromotionsBeingApplied(initialState.promotionsBeingApplied);
    setUserPromotionsMeta(initialState.userPromotionsMeta);
  }, [resetRequest]);

  const totalPromoCount = data?.reduce((totalCount, { count }) => (count ? totalCount + count : totalCount), 0) || 0;

  return {
    clearUserPromotions,
    fetchUserPromotions,
    isLoadingUserPromotions: isLoyaltyEnabled.current ? isLoading || isLoadingImages : false,
    promotionsBeingApplied,
    setPromotionsBeingApplied,
    totalPromoCount,
    userPromotions: data || defaultUserPromotions,
    userPromotionsMeta,
  };
};
